import * as React from 'react'

import { Box, SearchField, Table } from '@factorialco/deprecated-design-system'
import {
  SalaryRangesTableRowLegalEntitiesFragment,
  SalaryRangesTableRowRoleFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import Row from './Row'

const useFilteredSalaryRanges = createComputed(
  (
    salaryRanges: SalaryRangesTableRowRoleFragment['levels'][number]['salaryRanges'],
    search: string
  ) => salaryRanges.filter((x) => matchText(search, x.level?.name || ''))
)

interface Props {
  levels?: SalaryRangesTableRowRoleFragment['levels']
  legalEntities?: SalaryRangesTableRowLegalEntitiesFragment[]
  onEdit: (
    salaryRange: SalaryRangesTableRowRoleFragment['levels'][number]['salaryRanges'][number]
  ) => void
  onDeleted: () => void
}

const SalaryRangesTable: React.FC<Props> = ({ levels, onEdit, legalEntities, onDeleted }) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const salaryRanges = levels?.reduce<
    SalaryRangesTableRowRoleFragment['levels'][number]['salaryRanges']
  >((acc, x) => {
    return [...acc, ...x.salaryRanges]
  }, [])

  const filteredSalaryRanges = useFilteredSalaryRanges(salaryRanges || [], debouncedSearch).get()

  const sortedSalaryRanges = filteredSalaryRanges.sort((a, b) => {
    if (a.level?.order && b.level?.order) return a.level.order - b.level.order

    return a.id - b.id
  })

  const options = () => (
    <Box width="full">
      <SearchField
        variant="big"
        search={search}
        onSearch={onSearch}
        placeholder={i18n.t('common.search')}
      />
    </Box>
  )

  const rows = sortedSalaryRanges.map((x) => (
    <Row
      key={x.id}
      salaryRange={x}
      legalEntities={legalEntities}
      onEdit={() => onEdit(x)}
      onDeleted={onDeleted}
    />
  ))

  const tableContent = rows.length ? (
    rows
  ) : (
    <Table.Blank colSpan={7} text={i18n.t('job_catalog.salary_ranges.no_salary_ranges')} />
  )

  return (
    <Table fullWidth options={options}>
      <Table.Head>
        <Table.Row>
          <Table.HeadCell>{i18n.t('job_catalog.salary_ranges.job_level')}</Table.HeadCell>
          <Table.HeadCell colSpan={2}>
            {i18n.t('job_catalog.salary_ranges.legal_entities')}
          </Table.HeadCell>
          <Table.HeadCell>{i18n.t('job_catalog.salary_ranges.type')}</Table.HeadCell>
          <Table.HeadCell>{i18n.t('job_catalog.salary_ranges.amount')}</Table.HeadCell>
          <Table.HeadCell />
        </Table.Row>
      </Table.Head>
      <Table.Body>{tableContent}</Table.Body>
    </Table>
  )
}

export default observer(SalaryRangesTable)
